import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import { makeStyles } from '@material-ui/core';
import getThemeLookup from '../selectors/getThemeLookup';
import { OrderingSelectors, OrderingOperations } from 'polygon-ordering';

import '../styles/PurchaserDetails.css';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { ORDER_NOT_READY_REASON } from '../libs/polygon-ordering/src/constants';
import EVENTS from '../constants/events';
import { logEvent } from '../utils/analytics';
import SectionContainer from './SectionContainer';
const { getPurchaser, getPurchaserInvalidReasons, getRequiredPurchaserDetails } = OrderingSelectors;

interface IProps {
  id: PurchaserDetails;
  handleChange: (value: string) => void;
  touched: {
    [key: string]: boolean;
  };
  type?: string;
  onBlur: (id: string) => void;
  errorReason: ORDER_NOT_READY_REASON;
}

const PurchaserField: React.FC<IProps> = ({
  id,
  handleChange,
  touched,
  errorReason,
  onBlur,
  type,
}) => {
  const purchaser = useAppSelector(getPurchaser);
  const { t } = useTranslation();
  const p = useAppSelector(getThemeLookup);
  const purchaserInvalidReasons = useAppSelector(getPurchaserInvalidReasons);
  const requiredPurchaserDetails = useAppSelector(getRequiredPurchaserDetails);
  const placeholder = purchaser.effective[id];
  const inError = purchaserInvalidReasons.includes(errorReason);
  const required = requiredPurchaserDetails.includes(id);
  const value = purchaser.effective[id] || purchaser[id] || '';
  const showError = touched[id] && inError && Boolean(value);

  const useStyles = makeStyles(() => ({
    errors: {
      '& .MuiOutlinedInput-root': {
        '& .MuiOutlinedInput-notchedOutline': {
          border: `1px solid ${p('materialTextFieldErrorBorder', ['color']).color} !important`,
        },
      },
    },
    root: {
      '& ::-webkit-calendar-picker-indicator': {
        filter: `invert(${p('fieldIcons', ['color']).color})`,
      },
      //label - placeholder background color
      '& .MuiFormLabel-root': {
        backgroundColor: p('labelBackgroundColor', ['color']).color,
        borderRadius: 5,
        paddingLeft: 5,
        paddingRight: 5,
      },

      //text field errors
      '&  .MuiFormHelperText-root.Mui-error': {
        backgroundColor: p('helperBackground', ['color']).color,
        color: p('helperColor', ['color']).color,
      },
      // focused color for textfield with variant='outlined'
      '& .MuiOutlinedInput-root': {
        //textfield background color
        background: p('fieldBackground', ['color']).color,

        //text field border unfocused
        '& fieldset': {
          borderColor: p('fieldDefaultBorder', ['color']).color,
        },
        //text field border focused
        '&.Mui-focused fieldset': {
          borderColor: p('fieldBorderFocus', ['color']).color,
        },
        //text field border hover
        '&:hover fieldset': {
          borderColor: p('fieldHoverBorderColor', ['color']).color,
        },
      },
      // label color when unfocused
      '& .MuiInputLabel-root': {
        color: `${p('fieldDefaultBorder', ['color']).color} !important`,
      },
      // label color when focused
      '& .MuiInputLabel-root.Mui-focused': {
        color: `${p('fieldDefaultBorder', ['color']).color} !important`,
      },
      //checkbox
      '&$checked': {
        color: p('checkboxCheckedColor', ['color']).color,
      },
    },

    checked: {},
  }));

  const muiStyles = useStyles();

  const sharedInputProps = {
    InputProps: {
      style: {
        //textfield text filled colors
        color: p('textColor', ['color']).color,
        ...p('formTextFieldInput', ['color']),
      },
    },
    InputLabelProps: {
      style: {
        //textfield label - placeholder colors
        color: p('labelColor', ['color']).color,
      },
    },
  };

  const getHelperText = () => {
    if (showError) {
      return t(`purchaserDetailInvalid.${id}`);
    }

    if (required && !placeholder) {
      return t('field.requiredWarning');
    }

    return '';
  };

  const getErrorState = () => {
    if (showError) {
      return true;
    }

    if (required && !placeholder) {
      return true;
    }

    return false;
  };

  const getClassName = () => {
    if (showError) {
      return `${muiStyles.root} ${muiStyles.errors}`;
    }

    if (required && !placeholder) {
      return `${muiStyles.root} ${muiStyles.errors}`;
    }

    return muiStyles.root;
  };

  return (
    <TextField
      size="small"
      type={type}
      label={t(`field.purchaserDetails.${id}`)}
      placeholder={placeholder!}
      helperText={getHelperText()}
      error={getErrorState()}
      value={value}
      onChange={event => handleChange(event?.target?.value)}
      onBlur={() => onBlur(id)}
      fullWidth
      style={styles.inputField}
      variant="outlined"
      required={required}
      InputProps={sharedInputProps.InputProps}
      InputLabelProps={{
        shrink: true,
        ...sharedInputProps.InputLabelProps,
      }}
      className={getClassName()}
    />
  );
};

const PurchaserDetails = () => {
  const { setPurchaserName, setPurchaserFamilyName, setPurchaserMobile, setPurchaserEmail } =
    OrderingOperations;
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const dispatch = useAppDispatch();

  const handleBlur = (key: string) => {
    if (!touched[key]) {
      setTouched({ ...touched, [key]: true });
    }
  };

  const fields = [
    {
      id: 'name',
      onChange: (val: string) => {
        dispatch(setPurchaserName(val));
      },
      errorReason: ORDER_NOT_READY_REASON.PURCHASER_NAME_INVALID,
    },
    {
      id: 'familyName',
      onChange: (val: string) => {
        dispatch(setPurchaserFamilyName(val));
      },
      errorReason: ORDER_NOT_READY_REASON.PURCHASER_FAMILY_NAME_INVALID,
    },
    {
      id: 'mobile',
      type: 'tel',
      onChange: (val: string) => {
        dispatch(setPurchaserMobile(val));
      },
      errorReason: ORDER_NOT_READY_REASON.PURCHASER_MOBILE_INVALID,
    },
    {
      id: 'email',
      type: 'email',
      onChange: (val: string) => {
        dispatch(setPurchaserEmail(val));
      },
      errorReason: ORDER_NOT_READY_REASON.PURCHASER_EMAIL_INVALID,
    },
  ];

  return (
    <SectionContainer>
      {fields.map(field => (
        <PurchaserField
          key={field.id}
          id={field.id as PurchaserDetails}
          type={field.type}
          handleChange={(value: string) => {
            field.onChange(value);
            logEvent(EVENTS.CHANGE_PURCHASER_DETAILS);
          }}
          errorReason={field.errorReason}
          touched={touched}
          onBlur={handleBlur}
        />
      ))}
    </SectionContainer>
  );
};

const styles: Styles = {
  inputField: {
    marginTop: 10,
    marginBottom: 10,
  },
  inputLabel: {
    marginTop: -3,
    marginLeft: -5,
    paddingLeft: 10,
    paddingRight: 10,
    borderRadius: 4,
  },
  subInput: {
    borderRadius: 4,
  },
};

export default PurchaserDetails;
